import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Form, Select } from "../../../components/forms";
import { uploadFile } from "../../../services/fileService";
import parseError from "../../../utils/errorUtils";
import GoToProfilePopup from "./GoToProfilePopup";
import DocumentUpload from "./DocumentUpload";
import LoadingSpinner from "../../../components/loadingSpinner/LoadingSpinner";
import { useAuth } from "../../../utils/authUtils";

const KYCIdentificationForm = () => {
  const [initialValues] = useState({ id: [] });
  const navigate = useNavigate();
  const [errorText, setErrorText] = useState("");
  const [documentType, setDocumentType] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [clearFiles, setClearFiles] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const auth = useAuth();
  const [numberOfDocuments, setNumberOfDocuments] = useState(0)

  const handleNavigate = () => navigate("/user/order", { replace: true });

  const toggleModal = () => {
    setShowModal((prev) => !prev);
  };

  const handleDocumentTypeChange = (e) => {
    setDocumentType(e.target.value);
    setUploadedFile(null);
    setClearFiles(true);
  };

  const handleSubmit = async (values, { resetForm }) => {
    try {
      if (!documentType) {
        setErrorText("Please select a document type.");
        toast.error("Please select a document type.");
        return;
      }

      if (!uploadedFile) {
        setErrorText("Please upload a file.");
        toast.error("Please upload a document.");
        return;
      }

      const formData = new FormData();
      formData.append("action", "PATIENT_KYC_DOCUMENTS");
      formData.append("category", documentType);
      formData.append("files", uploadedFile);

      setIsSubmitting(true);
      const res = await uploadFile(formData);

      if (res.error) {
        toast.error(parseError(res.error, "Couldn't upload the file."));
        setErrorText("File upload failed. Please try again.");
        setIsSubmitting(false);
        return;
      }

      !isMobile && toast.success("File uploaded successfully!");
      setShowModal(true);
      resetForm();
      setUploadedFile(null);
      setDocumentType("");
      setIsSubmitting(false);
    } catch (error) {
      setIsSubmitting(false);
      console.error("Error uploading file:", error);
      setErrorText("An error occurred during upload. Please try again.");
    }
  };

  useEffect(() => {
    if (!auth?.isLogged) {
      navigate("/onboarding/eligibility-flow/condition");
    }
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    setIsMobile(isMobile);
  }, []);

  useEffect(() => {
    if (clearFiles) {
      setClearFiles(false);
    }
  }, [clearFiles]);

  const validateFiles = (values) => {
    values?.documents && setNumberOfDocuments(values?.documents?.length)
  }

  return (
    <div className="d-flex flex-column h-100">
      <h4 className="mb-2 fw-bold">Upload Identification document for KYC</h4>
      <div className="container px-0 d-flex flex-column justify-content-between">
        <div className="column col-md-12">
          <p className="text-gray-1 mb-3">
            An Accepted form of Identification (Appropriate ID includes a valid
            passport, photo driving license, or proof of age card, such as a
            PASS card from the national Proof of Age Standards Scheme). You may
            choose to upload any <span className="text-primary fw-bold">one of the three recommended documents</span>.
          </p>
        </div>
      </div>
      <div className="d-flex mt-4 h-100">
        <Form
          initialValues={{ message: "" }}
          onSubmit={handleSubmit}
          enableReinitialize
          className="d-flex"
        >
          <div className="container px-0 d-flex flex-column justify-content-between">
            <div className="column col-md-12 col-lg-12 col-xl-9">
              <div className="row">
                <div className="col-md-12">
                  <label className="mb-2">Document type</label>
                  <div className="d-flex flex-column flex-md-row gap-5 align-items-center">
                    <Select
                      name="documentType"
                      placeholder="Select document type"
                      className="form-select shadow-none py-2 bg-transparent border card-border fs-6 rounded pe-2 w-100 custom-placeholder"
                      errorClass="error-text mt-1"
                      value={documentType}
                      onChange={handleDocumentTypeChange}
                      options={[
                        { value: "driving_license", label: "Driving license" },
                        {
                          value: "identification_of_residence",
                          label: "Identification of Residence (ID)",
                        },
                        { value: "passport", label: "Passport" },
                      ]}
                    />
                  </div>
                </div>
              </div>
              <div className="row mt-3">
                <div className=" col-md-12 col-lg-12 col-xl-12 mb-md-5 mb-xl-0">
                  <label className="mb-2">Upload document</label>
                  <DocumentUpload
                    name="documents"
                    multiple={false}
                    fileLimit={1}
                    acceptedTypes={[
                      "image/jpeg",
                      "image/png",
                      "image/jpg",
                      "application/pdf",
                    ]}
                    clearFiles={clearFiles}
                    onFileSelect={(file) => {
                      setUploadedFile(file);
                    }}
                    getFiles={validateFiles}
                  />
                </div>
              </div>
            </div>
            <div className="column d-flex flex-row justify-content-center justify-content-md-end align-items-center">
              <div className="d-flex flex-column-reverse flex-md-row footer-div px-5 px-md-0 py-4 py-md-0 fixed-single-footer justify-content-md-end">
                <button
                  className="text-primary-600 bg-transparent pt-2 pb-0 px-4 btn-sm w-md-fit h-fit fw-bold border-0 rounded text-nowrap"
                  onClick={handleNavigate}
                >
                  <p className="mb-0 fw-bold fs-6">Upload later</p>
                </button>
                <button
                  className={`bg-primary-600 text-white py-2 px-5 btn-sm w-md-fit h-fit fw-bold fs-6 border-0 rounded text-nowrap
                    ${numberOfDocuments === 0 ? "opacity-50" : "opacity-100"}`
                    }
                  type="submit"
                  disabled={numberOfDocuments === 0 || isSubmitting}
                >
                  {isSubmitting ? (
                    <LoadingSpinner loadingText="Submit" />
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </div>
          </div>
        </Form>
      </div>
      {showModal && <GoToProfilePopup show={showModal} onClose={toggleModal} />}
    </div>
  );
};

export default KYCIdentificationForm;
